/**
 * The one "needs you" count (docs/ux/review/interaction-visual.md #5). Every surface that shows
 * how many things wait on the user — the rail and tab-bar badges, the PWA badge, the Inbox's
 * "Needs you" group, Now's needs-you line, Overview tiles and totals, project tab badges and asks
 * panels, Voice — reads it from here, so the numbers always agree. (Now's run groups count runs,
 * a different unit, and say so.)
 *
 * Definition: an Inbox row (`buildInboxRows`: decision entities joined with the hub's asks, so a
 * question is counted once) that is open, not snoozed, not resolved by a newer report, and
 * **blocks a run**. Rows that block nothing (cards, builds, proposals, "when convenient" asks)
 * are listed in the Inbox but are not counted.
 */
import type { InboxRow } from './now-inbox.js'

export type NeedsYouRow = Pick<InboxRow, 'key' | 'blocking' | 'projectId' | 'item'> &
  Partial<Pick<InboxRow, 'kind' | 'freshness' | 'reasons' | 'title'>>

export const isNeedsYou = (r: Pick<InboxRow, 'blocking'>) => r.blocking

/**
 * Blocked vs needs you (docs/ux/design-language.md §7): a blocked row can't continue until
 * something outside a decision is fixed — environment, disk, permission, a missing secret, a failed
 * landing check. It is counted in the needs-you total (it waits on you) but shown in red-orange,
 * in its own group, with the reason in words.
 */
export function isBlockedRow(r: Partial<Pick<InboxRow, 'kind' | 'freshness'>>): boolean {
  return r.kind === 'blocked' || (r.kind === 'landing' && !!r.freshness?.landing)
}

/** The reason in words: "disk 12 GB free on Photon", "landing checks failed". */
export function blockedReason(r: Partial<Pick<InboxRow, 'reasons' | 'title'>>): string {
  const title = r.title?.replace(/^Blocked:\s*/, '')
  return title || r.reasons?.[0] || 'needs a fix'
}

/** The rows that need the user, optionally for one project. */
export function needsYouRows<R extends NeedsYouRow>(rows: readonly R[], projectId?: string): R[] {
  return rows.filter((r) => isNeedsYou(r) && (projectId === undefined || r.projectId === projectId))
}

export function needsYouCount(rows: readonly NeedsYouRow[], projectId?: string): number {
  return needsYouRows(rows, projectId).length
}

/** Per-project counts (rows without a project are only in the total). */
export function needsYouByProject(rows: readonly NeedsYouRow[]): Map<string, number> {
  const out = new Map<string, number>()
  for (const r of rows)
    if (isNeedsYou(r) && r.projectId) out.set(r.projectId, (out.get(r.projectId) ?? 0) + 1)
  return out
}

/** Hub item ids behind the counted rows (the PWA badge clears notifications by these). */
export function needsYouItemIds(rows: readonly NeedsYouRow[]): string[] {
  return needsYouRows(rows).map((r) => r.item?.itemId ?? r.item?.id ?? r.key)
}

export interface NeedsYou {
  /** Everything that waits on you: blocked + decisions (the PWA badge, "N need you"). */
  total: number
  of: (projectId: string) => number
  byProject: ReadonlyMap<string, number>
  rows: readonly NeedsYouRow[]
  /** The red-orange part of `total`: blocked rows. */
  blocked: number
  blockedOf: (projectId: string) => number
  /** The amber part of `total`: rows waiting on a decision (total − blocked). */
  decide: number
  decideOf: (projectId: string) => number
}

export function needsYou(rows: readonly NeedsYouRow[]): NeedsYou {
  const byProject = needsYouByProject(rows)
  const counted = needsYouRows(rows)
  const blockedRows = counted.filter(isBlockedRow)
  const blockedBy = needsYouByProject(blockedRows)
  const of = (id: string) => byProject.get(id) ?? 0
  const blockedOf = (id: string) => blockedBy.get(id) ?? 0
  return {
    total: counted.length,
    of,
    byProject,
    rows: counted,
    blocked: blockedRows.length,
    blockedOf,
    decide: counted.length - blockedRows.length,
    decideOf: (id) => of(id) - blockedOf(id),
  }
}
